import { apiFetch } from "../api/client";
import { french, type Say } from "../settings/say";

/**
 * LES CONFÉRENCES DE CE PARTICIPANT, ET SON CRÉNEAU.
 *
 *   GET /domains/:d/events/:e/conferences/v2/users/:u          apix
 *   PUT /domains/:d/events/:e/conferences/v2/users/:u/slots/:s apix
 *
 * ⚠️ DEUX SYSTÈMES DE CONFÉRENCES COEXISTENT DANS LA V1, ET C'EST LE SECOND QU'ON
 * REPREND. L'ancien (`FO_MEETING_CONFERENCES_IS_ACTIVE`, `screens/Conferences/`)
 * demande trois requêtes et recalcule ses règles dans le navigateur ; le second
 * (`FO_MEETING_CONFERENCES_V2_IS_ACTIVE`, `screens/steps/conferencesV2/`) tient dans
 * une route faite pour cet écran. C'est sa clé que l'événement de test déclare, et
 * c'est la seule des deux dont le serveur calcule lui-même les verrous.
 *
 * ⚠️ ET C'EST LUI QUI LES CALCULE — ON NE LES REFAIT PAS. Complet, chevauchement avec
 * un créneau déjà retenu, quota de conférences atteint, collègue déjà inscrit : la
 * route rend `disabled` sur chaque créneau et sur chaque conférence, après avoir lu la
 * base ET les réglages. Les rejouer ici donnerait une seconde règle, plus pauvre — le
 * chevauchement, par exemple, se juge contre TOUS les créneaux retenus, y compris ceux
 * d'une autre conférence — et l'écran interdirait ou autoriserait ce que le serveur
 * fait l'inverse.
 *
 * ⚠️ LE MAXIMUM EST CELUI DE L'ORGANISATEUR, ET LA ROUTE LE REND. Elle codait deux en
 * dur pour tout événement, et l'écran ne le découvrait qu'au refus de la troisième. Elle
 * lit maintenant `FO_MEETING_CONFERENCES_MAX` (réglé au back office), sans limite s'il
 * est absent comme la V1, et rend `max` à côté de la liste : c'est ce qui permet de
 * l'ANNONCER avant qu'on l'atteigne (`maxAnnounce`).
 *
 * ⚠️ « UN SEUL COLLABORATEUR PAR SOCIÉTÉ » NE PEUT PAS ÊTRE RE-CLÉ.
 * `FO_MEETING_CONFERENCES_EMPLOYEES_ONE_ONLY` est lu par le SERVEUR
 * (`hooks/useSettingsFo`), dans la même table que celle où la V2 écrit, mais sous son
 * nom V1. Une copie préfixée `FO_MEETING_V2_` serait invisible de lui : la règle reste
 * donc réglée au back office, et le panneau ne prétend pas la porter.
 */

/** Un créneau, tel que la route le rend — verrous compris. */
export type Slot = {
  id: number;
  /**
   * Dates ISO, construites depuis l'ÉPOQUE MURALE de la colonne (`startAt` en
   * millisecondes = l'heure saisie au back office, exprimée en UTC — voir
   * `api/src/helpers/wallEpoch.js`). Leur partie UTC EST l'heure à afficher : tout
   * formatage passe par `timeZone: "UTC"`, jamais par le fuseau du lecteur.
   */
  start: string;
  end: string;
  /** Ce participant y est inscrit. */
  selected: boolean;
  /** Le serveur refuse ce créneau — voir `motDuCreneau` pour la raison. */
  closed: boolean;
  /** Le quota du créneau est atteint. */
  full: boolean;
  /** Il chevauche un créneau déjà retenu, ici ou ailleurs. */
  overlaps: boolean;
};

export type Conference = {
  id: number;
  title: string;
  /** La présentation, telle que le back office l'a écrite. Vide s'il n'y en a pas. */
  html: string;
  /** Ce participant a un créneau dans cette conférence. */
  selected: boolean;
  /** Un collègue de sa société y est déjà, et l'événement n'en veut qu'un. */
  registeredColleague: boolean;
  /** Il a atteint le nombre de conférences que la route autorise. */
  maximumReached: boolean;
  /** Aucun créneau n'est ouvert. */
  closed: boolean;
  slots: Slot[];
};

type RawSlot = {
  id?: number;
  startAt?: string | number | null;
  endAt?: string | number | null;
  registered?: unknown;
  disabled?: unknown;
  isFull?: unknown;
  overlap?: unknown;
};

type RawConference = {
  id?: number;
  title?: string | null;
  /** Colonne JSON : `{ html }` sur les lignes récentes, parfois une chaîne. */
  description?: { html?: string | null } | string | null;
  registered?: unknown;
  coworkerRegistered?: unknown;
  maxReached?: unknown;
  disabled?: unknown;
  slots?: RawSlot[] | null;
};

const yes = (v: unknown) => v === true || v === 1;
/* Une date manquante donne une chaîne vide, que `creneauLong` rend telle quelle
   plutôt que « Invalid Date ». */
const when = (v: unknown) =>
  typeof v === "string"
    ? v
    : typeof v === "number"
      ? new Date(v).toISOString()
      : "";

function conferenceDe(raw: RawConference): Conference | null {
  if (typeof raw?.id !== "number") return null;

  const description = raw.description;
  const html =
    typeof description === "string" ? description : (description?.html ?? "");

  return {
    id: raw.id,
    // Une conférence sans titre reste choisissable : elle se nomme par son
    // identifiant plutôt que par un blanc au milieu de la liste.
    title: (raw.title ?? "").trim() || `Conférence ${raw.id}`,
    html,
    selected: yes(raw.registered),
    registeredColleague: yes(raw.coworkerRegistered),
    maximumReached: yes(raw.maxReached),
    closed: yes(raw.disabled),
    slots: (raw.slots ?? []).flatMap((s) =>
      typeof s?.id !== "number"
        ? []
        : [
            {
              id: s.id,
              start: when(s.startAt),
              end: when(s.endAt),
              selected: yes(s.registered),
              closed: yes(s.disabled),
              full: yes(s.isFull),
              overlaps: yes(s.overlap),
            },
          ],
    ),
  };
}

/** Ce que la route rend : les conférences, et combien on peut en retenir. */
export type ConferenceList = {
  conferences: Conference[];
  /** Le nombre de conférences qu'un participant peut retenir ; `null` = aucune limite. */
  max: number | null;
};

export async function fetchConferences(
  domainId: number,
  eventId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<ConferenceList> {
  const answer = await apiFetch<{
    conferences?: RawConference[];
    max?: unknown;
  }>(
    `/domains/${domainId}/events/${eventId}/conferences/v2/users/${userId}`,
    { api: "apix", signal },
  );
  return {
    conferences: (answer?.conferences ?? []).flatMap((c) => {
      const conference = conferenceDe(c);
      return conference ? [conference] : [];
    }),
    max:
      typeof answer?.max === "number" && answer.max > 0 ? answer.max : null,
  };
}

/**
 * LA LIMITE, DITE AVANT D'ÊTRE ATTEINTE — rien quand il n'y en a pas.
 *
 * ⚠️ `SLOT_MAX_REACHED` NE SUFFIT PAS : il ne paraît qu'une fois la limite atteinte, donc
 * au moment où la troisième conférence est refusée. Le participant compose son programme
 * sans savoir qu'il doit choisir ; c'est dans l'introduction qu'il faut le lui dire.
 *
 * ⚠️ LE SINGULIER EST UNE CLÉ À PART, comme `N_SELECTED_ONE` : « jusqu'à 1 conférences »
 * se lit mal, et toutes les langues n'accordent pas au même seuil.
 */
export function maxAnnounce(max: number | null, say: Say = french): string {
  if (max === null) return "";
  return max === 1
    ? say("CONFERENCES_UP_TO_ONE", "Vous pouvez retenir une seule conférence.")
    : say("CONFERENCES_UP_TO", "Vous pouvez retenir jusqu'à $1 conférences.", [
        max,
      ]);
}

/**
 * POSE OU RETIRE LE CRÉNEAU — un seul appel, et il BASCULE.
 *
 * ⚠️ LA ROUTE DÉCIDE, PAS NOUS. Elle supprime d'abord l'inscription du participant à
 * CETTE conférence, puis réinsère — sauf si le créneau envoyé est celui qu'il avait
 * déjà, auquel cas elle s'arrête là. Envoyer le même créneau désinscrit donc, en changer
 * déplace, et une conférence n'en retient jamais deux. Il n'y a rien à composer ici :
 * une « désinscription » qui passerait par un DELETE inventé n'existe pas.
 */
export async function placeSlot(
  domainId: number,
  eventId: number,
  userId: number,
  slotId: number,
): Promise<void> {
  await apiFetch(
    `/domains/${domainId}/events/${eventId}/conferences/v2/users/${userId}/slots/${slotId}`,
    { api: "apix", method: "PUT" },
  );
}

/**
 * CE QUE LA SÉANCE ANNONCE, ET SI ELLE RÉPOND AU CLIC.
 *
 * ⚠️ UNE PHRASE, PAS UN MOT — et c'est ce que le prototype demande dans `.conf__e`.
 * « Indisponible » est vrai et inutile : ce que le participant veut savoir, c'est s'il
 * doit attendre, se libérer, ou renoncer. Chaque cause a donc sa phrase.
 *
 * ⚠️ ET « COMPLET » N'EST PAS UNE CAUSE PERSONNELLE. Le quota d'un créneau peut se
 * rouvrir si quelqu'un se désinscrit ; un chevauchement, un collègue déjà inscrit ou
 * un maximum atteint ne se rouvriront jamais tout seuls. Les confondre envoie
 * quelqu'un attendre une place qui ne viendra pas.
 *
 * ⚠️ RIEN QUAND ELLE EST SIMPLEMENT LIBRE, comme le prototype, qui n'ajoute alors
 * aucun `<small>`. Une ligne « Disponible » sous chaque séance disponible ne
 * distingue rien : le silence est ce qui fait ressortir les autres.
 */
export function slotState(
  conference: Conference,
  slot: Slot,
  say: Say = french,
): { state: string; open: boolean } {
  if (slot.selected)
    return { state: say("YOU_ARE_REGISTERED", "Vous êtes inscrit"), open: true };
  if (!slot.closed) return { state: "", open: true };

  /* La cause la plus PRÉCISE d'abord : plusieurs peuvent valoir en même temps, et
     celle sur laquelle le participant peut agir doit passer devant « complet ». */
  const state = conference.registeredColleague
    ? say(
        "SLOT_COLLEAGUE_REGISTERED",
        "Un collaborateur de votre société y est déjà inscrit",
      )
    : conference.maximumReached
      ? say("SLOT_MAX_REACHED", "Vous avez atteint le maximum de conférences")
      : slot.overlaps
        ? say("SLOT_OVERLAPS", "Chevauche une conférence que vous avez retenue")
        : slot.full
          ? say("SLOT_FULL", "Complet")
          : // Le serveur a fermé le créneau pour une raison qu'il ne détaille pas :
            // on ne l'invente pas, mais on ne se tait pas non plus.
            say("SLOT_UNAVAILABLE", "Indisponible");
  return { state, open: false };
}

/** Une ligne du programme : une conférence, à une heure, un jour donné. */
export type Session = {
  conference: Conference;
  slot: Slot;
  /** « 9:30 – 10:15 », en tête de la ligne. */
  hours: string;
  /** Sa présentation, réduite au texte — le prototype lui donne une ligne. */
  detail: string;
  state: string;
  open: boolean;
};

/** Un jour du programme, et ce qui s'y passe. */
export type Day = {
  /** « Mardi 14 juin », tel qu'il s'écrit en tête du groupe. */
  day: string;
  /**
   * ⚠️ LA CLÉ DU REGROUPEMENT, ET SURTOUT PAS LE LIBELLÉ. Le libellé est FAIT pour être
   * lu, donc il abrège : il portait « Lun. 23 » sans le mois, et deux lundis 23 de deux
   * mois différents tombaient dans la MÊME journée — leurs séances mélangées sous un
   * seul en-tête, sans que rien ne le signale. Écrire le mois n'y suffit pas non plus :
   * deux 23 octobre de deux ANNÉES différentes se confondraient à leur tour, et
   * l'événement de test en porte justement (2023 et 2026). Le jour ISO ne peut pas
   * collisionner.
   */
  key: string;
  /** Combien de séances y sont retenues — le compte à droite de l'en-tête. */
  selected: number;
  sessions: Session[];
};

const instant = (iso: string) => new Date(iso).getTime();

/**
 * LE JOUR D'UN CRÉNEAU, EN ISO — `2026-06-02`.
 *
 * ⚠️ EN UTC, comme le libellé qu'il accompagne : la valeur est une ÉPOQUE MURALE (voir
 * `Slot`), donc sa partie UTC est le jour saisi au back office. Lu dans le fuseau du
 * lecteur, un créneau de 23 h changeait de journée pour qui le lit depuis un autre
 * fuseau — et l'affichage et le regroupement doivent lire la même horloge.
 */
function isoDay(start: string): string {
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return start;
  return d.toISOString().slice(0, 10);
}

/**
 * LE PROGRAMME, TEL QUE LE PROTOTYPE LE DISPOSE : à plat, par jour, dans l'ordre des
 * heures.
 *
 * ⚠️ UNE LIGNE PAR (CONFÉRENCE, CRÉNEAU), ET NON PAR CONFÉRENCE. C'est la traduction
 * du prototype, dont l'unité est « une séance à une heure un jour donné » — il ignore
 * qu'une conférence puisse être rejouée. L'API, elle, le sait : une conférence porte
 * plusieurs créneaux et le serveur n'en retient qu'un par conférence. Mise à plat, la
 * même conférence peut donc apparaître deux fois, à deux heures — ce qui est
 * exactement ce qu'elle est pour qui compose son emploi du temps, et ce qui permet de
 * la classer par jour. Choisir l'autre séance déplace l'inscription ; c'est la route
 * qui s'en charge.
 *
 * ⚠️ LES JOURS DANS L'ORDRE RENCONTRÉ, APRÈS TRI PAR HEURE — la méthode du prototype
 * (`if (jours.indexOf(c.j) < 0) jours.push(c.j)`). Trier les LIBELLÉS les rangerait par
 * alphabet, et « mer. 15 » passerait avant « mar. 14 ».
 */
export function programme(
  conferences: ReadonlyArray<Conference>,
  locale: string,
  say: Say = french,
): Day[] {
  const sessions = conferences
    .flatMap((conference) =>
      conference.slots.map((slot) => {
        const { state, open } = slotState(conference, slot, say);
        return {
          conference,
          slot,
          hours: hoursOf(slot.start, slot.end, locale),
          detail: asText(conference.html),
          state,
          open,
        };
      }),
    )
    .sort((a, b) => instant(a.slot.start) - instant(b.slot.start));

  const days: Day[] = [];
  for (const session of sessions) {
    const key = isoDay(session.slot.start);
    let group = days.find((j) => j.key === key);
    if (!group) {
      group = {
        day: slotDay(session.slot.start, locale),
        key,
        selected: 0,
        sessions: [],
      };
      days.push(group);
    }
    group.sessions.push(session);
    if (session.slot.selected) group.selected += 1;
  }
  return days;
}

/**
 * LA PRÉSENTATION, RÉDUITE À SON TEXTE.
 *
 * ⚠️ LA LIGNE EST DANS UN BOUTON, et du HTML libre n'y a pas sa place : un lien ou un
 * bouton imbriqué dans un bouton n'est pas du balisage valide, le navigateur défait
 * l'imbrication, et la ligne cesse d'être cliquable. Le prototype met une ligne de
 * texte à cet endroit — chez lui la salle, que l'API ne porte pas ; on y met ce que le
 * produit a.
 *
 * ⚠️ SANS `DOMParser`, ET C'EST SON TEST QUI L'A IMPOSÉ. Il n'existe pas hors
 * navigateur : la fonction retombait alors sur le markup BRUT, donc « <p>Salle… »
 * affiché en toutes lettres. Un repli qui produit du charabia est pire que pas de
 * repli, et il ne se serait vu qu'au premier rendu côté serveur.
 *
 * ⚠️ CE N'EST PAS DE L'ASSAINISSEMENT, ET LA NUANCE COMPTE. Ce texte part dans un nœud
 * TEXTE, jamais dans du markup : rien de ce qui échappe à ces règles ne peut s'exécuter
 * — au pire un fragment de travers reste lisible. Un `<a title="a>b">` couperait au
 * mauvais chevron ; sorti d'un éditeur de back-office, ça n'arrive pas, et le prix
 * serait un mot en trop dans une ligne grise.
 */
export function asText(html: string): string {
  return (
    html
      // Une balise vaut un ESPACE et non rien : « <p>a</p><p>b</p> » donne « a b »,
      // pas « ab ».
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;/gi, "'")
      // ⚠️ EN DERNIER. Décodée en premier, « &amp;lt; » deviendrait « < ».
      .replace(/&amp;/gi, "&")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/**
 * L'HEURE DE DÉBUT ET DE FIN — « 9:30 – 10:15 ».
 *
 * ⚠️ PAR `Intl`, ET NON EN « 9h30 » COMME LE PROTOTYPE. Le « h » est la convention
 * FRANÇAISE, et cet écran se lit dans la langue du participant : la maquette est
 * française, le produit ne l'est pas. C'est la règle que suivent déjà toutes les
 * autres dates d'ici (`funnel/dates.ts`, `settings/complete.ts`). Ce qui compte de la
 * maquette — l'heure en tête, en gras, chiffres alignés — reste.
 *
 * ⚠️ EN UTC, ET CE N'EST PAS UN FUSEAU : c'est l'heure SAISIE. La colonne porte une
 * époque murale (voir `Slot`), donc « 14:00 » tapé au back office s'affiche « 14:00 »
 * à Paris, à Séoul ou sur un serveur en UTC. Lue dans le fuseau du lecteur, l'heure
 * variait d'un participant à l'autre — l'ambiguïté que la doctrine supprime.
 */
export function hoursOf(start: string, end: string, locale: string): string {
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return start;
  const f = new Date(end);

  let format: Intl.DateTimeFormat;
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  };
  try {
    format = new Intl.DateTimeFormat(locale, options);
  } catch {
    format = new Intl.DateTimeFormat(undefined, options);
  }

  return Number.isNaN(f.getTime())
    ? format.format(d)
    : `${format.format(d)} – ${format.format(f)}`;
}

/**
 * LE JOUR D'UN CRÉNEAU — « mar. 14 », l'en-tête de groupe du prototype.
 *
 * ⚠️ EN UTC, comme les JOURS de l'événement (`funnel/dates.ts`) et pour la même raison
 * désormais : un créneau porte une époque MURALE (voir `Slot`), c'est-à-dire le jour et
 * l'heure saisis, qu'aucun fuseau ne doit décaler. Il a été un instant rangé sous le
 * jour de l'horloge du lecteur ; ce n'est plus le cas.
 */
export function slotDay(start: string, locale: string): string {
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return start;

  /**
   * ⚠️ LE MÊME FORMAT QUE LA BILLETTERIE (`jourLong`) — « Lundi 23 juin ». Les deux
   * écrans posent la même question, jour par jour ; ils l'écrivaient de deux façons,
   * « Lun. 23 » ici et « Lundi 21 septembre » là.
   *
   * ⚠️ ET IL A FALLU CE FORMAT-LÀ POUR VOIR UN REGROUPEMENT FAUX. Cette chaîne SERVAIT
   * de clé de groupe : « Lun. 23 » ne porte pas le mois, donc deux lundis 23 de deux
   * mois différents tombaient dans la même journée. Le libellé abrège parce qu'il est
   * fait pour être lu ; le regroupement se fait désormais sur le jour ISO (`jourISO`),
   * qui, lui, ne peut pas collisionner.
   *
   * Le fuseau est l'UTC de l'époque murale — c'est la règle expliquée juste au-dessus.
   */
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  };
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat(locale, options);
  } catch {
    format = new Intl.DateTimeFormat(undefined, options);
  }

  const written = format.format(d);
  const [first, ...remaining] = [...written];
  // Majuscule initiale, dans la langue RÉSOLUE : voir `jourLong`, où la garde manquait.
  const selected = format.resolvedOptions().locale;
  return first
    ? first.toLocaleUpperCase(selected) + remaining.join("")
    : written;
}

/**
 * UN CRÉNEAU, ÉCRIT : « lun. 21 sept. · 09:30 – 10:15 ».
 *
 * ⚠️ LE MOIS EN LETTRES, JAMAIS EN CHIFFRES (bug #490). La langue arrive en « EN », qu'`Intl`
 * lit à l'américaine : « 12/05 » y voulait dire le 5 décembre, sur un site qui se présente
 * en anglais britannique. En lettres, l'ordre du jour et du mois ne trompe plus personne,
 * dans aucune langue, et la date se lit comme à l'étape de choix.
 *
 * ⚠️ EN UTC, comme `hoursOf` et pour la même raison : l'heure qui compte est celle qui
 * a été SAISIE, pas celle de l'horloge de qui lit. La V1 fait pareil (`dayjs.utc`).
 */
export function longSlot(
  start: string,
  end: string,
  locale: string,
): string {
  const d = new Date(start);
  const f = new Date(end);
  if (Number.isNaN(d.getTime())) return start;

  const format = (options: Intl.DateTimeFormatOptions) => {
    const utc = { ...options, timeZone: "UTC" };
    try {
      return new Intl.DateTimeFormat(locale, utc);
    } catch {
      // Une étiquette de langue illisible ne doit pas faire tomber l'écran.
      return new Intl.DateTimeFormat(undefined, utc);
    }
  };

  const day = format({ weekday: "short", day: "numeric", month: "short" });
  const hour = format({ hour: "2-digit", minute: "2-digit" });

  const bornes = Number.isNaN(f.getTime())
    ? hour.format(d)
    : `${hour.format(d)} – ${hour.format(f)}`;
  return `${day.format(d)} · ${bornes}`;
}
